import { useApolloClient } from "@apollo/client";
import { LabeledValue } from "@msys/ui";
import { Box, Button, Chip, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useCategories } from "./useCategories.js";

interface Props {
  categoryIds: string[];
  label?: string;
  leftIcon?: React.ReactNode;
  rightButton?: React.ReactNode;
  required?: boolean;
  addParentsWhenNotIncluded?: boolean;
  removeChildrenIfIncluded?: boolean;
  excludeDeletedCategories?: boolean;
}

const LIMIT = 5;

export const CategoriesLabeledValue = ({
  categoryIds,
  label,
  leftIcon,
  rightButton,
  required = false,
  addParentsWhenNotIncluded = true,
  removeChildrenIfIncluded = true,
  excludeDeletedCategories = true,
}: Props) => {
  const { t } = useTranslate(["Categories", "Global"]);
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      width="100%"
    >
      <Stack
        direction="row"
        alignItems="center"
        minWidth={0}
        spacing={1}
        flex={1}
      >
        <CategoriesChips
          categoryIds={categoryIds}
          label={label ?? t("Categories", { ns: "Categories" })}
          required={required}
          removeChildrenIfIncluded={removeChildrenIfIncluded}
          addParentsWhenNotIncluded={addParentsWhenNotIncluded}
          excludeDeletedCategories={excludeDeletedCategories}
        />
        {leftIcon}
      </Stack>
      {rightButton ? <Box display="flex">{rightButton}</Box> : null}
    </Stack>
  );
};

export function CategoriesChips({
  categoryIds,
  label,
  required,
  addParentsWhenNotIncluded,
  removeChildrenIfIncluded,
  excludeDeletedCategories,
}: {
  categoryIds: string[];
  label?: string;
  required?: boolean;
  addParentsWhenNotIncluded?: boolean;
  removeChildrenIfIncluded?: boolean;
  excludeDeletedCategories?: boolean;
}) {
  const { t } = useTranslate(["Categories", "Global"]);

  const [showMore, setShowMore] = React.useState(false);
  const { resolveCategories, loading } = useCategories();

  const categories =
    categoryIds.length > 0
      ? resolveCategories(categoryIds, {
          addParentsWhenNotIncluded,
          removeChildrenIfIncluded,
          excludeDeletedCategories,
        })
      : [];

  const chips = loading ? (
    ""
  ) : categories.length > 0 ? (
    <Stack
      direction="row"
      spacing={0.5}
      flexWrap="wrap"
      useFlexGap
      alignItems="center"
    >
      {(showMore || categories.length <= LIMIT
        ? categories
        : categories.slice(0, LIMIT - 1)
      ).map(category => (
        <Chip
          key={category.id}
          label={category.name}
          size="small"
          variant="outlined"
          color="primary"
        />
      ))}
      {categories.length > LIMIT && (
        <>
          {!showMore && (
            <span>
              …
              {t("and {count} more", {
                ns: "Global",
                count: categories.length - (LIMIT - 1),
              })}
            </span>
          )}
          <Button
            onClick={() => setShowMore(!showMore)}
            variant="text"
            color="secondary"
            size="extra-small"
          >
            {showMore
              ? t("Show less", { ns: "Global" })
              : t("Show more", { ns: "Global" })}
          </Button>
        </>
      )}
    </Stack>
  ) : (
    t("Not set", { ns: "Global" })
  );

  return label ? (
    <LabeledValue
      label={label + (required ? " *" : "")}
      notSet={!categoryIds.length}
    >
      {chips}
    </LabeledValue>
  ) : (
    chips
  );
}
